import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import ProductList from './components/products/ProductList';
import NotFound from './components/pages/NotFound';
import DashboardLayout from './layouts/DashboardLayout';
import AppTheme from './theme/AppTheme';
import { alpha, checkboxClasses, CssBaseline, iconButtonClasses, inputBaseClasses, LinearProgress, listClasses, listItemButtonClasses, listItemIconClasses, listSubheaderClasses, menuItemClasses, paperClasses, tablePaginationClasses, typographyClasses } from '@mui/material';
import NotificationsProvider from './hooks/useNotifications/NotificationsProvider';
import DialogsProvider from './hooks/useDialogs/DialogsProvider';
import { brand, gray } from './theme/themePrimitives';
import { pickersDayClasses, yearCalendarClasses } from '@mui/x-date-pickers-pro';
import { gridClasses } from '@mui/x-data-grid-pro';
import IngredientList from './components/items/ItemList';
import InventoryItemList from './components/inventoryItems/InventoryItemList';
import ProductCreate from './components/products/ProductCreate';
import ProductEdit from './components/products/ProductEdit';
import InventoryItemEdit from './components/inventoryItems/InventoryItemEdit';
import IngredientCreate from './components/items/ItemCreate';
import IngredientEdit from './components/items/ItemEdit';
import ProductShow from './components/products/ProductShow';
import InventoryItemShow from './components/inventoryItems/InventoryItemShow';
import IngredientShow from './components/items/ItemShow';
import BatchList from './components/batches/BatchList';
import BatchShow from './components/batches/BatchShow';
import BatchCreate from './components/batches/BatchCreate';
import ContactList from './components/contacts/ContactList';
import ContactShow from './components/contacts/ContactShow';
import ContactCreate from './components/contacts/ContactCreate';
import ContactEdit from './components/contacts/ContactEdit';
import VendorList from './components/vendors/VendorList';
import VendorShow from './components/vendors/VendorShow';
import VendorCreate from './components/vendors/VendorCreate';
import VendorEdit from './components/vendors/VendorEdit';
import CustomerList from './components/customers/CustomerList';
import CustomerEdit from './components/customers/CustomerEdit';
import CustomerShow from './components/customers/CustomerShow';
import CustomerCreate from './components/customers/CustomerCreate';
import PurchaseOrderList from './components/purchaseOrders/PurchaseOrderList';
import PurchaseOrderShow from './components/purchaseOrders/PurchaseOrderShow';
import PurchaseOrderCreate from './components/purchaseOrders/PurchaseOrderCreate';
import Login from './components/pages/Login';
import MainLayout from './layouts/MainLayout';
import Register from './components/pages/Register';
import Home from './components/pages/Home';
import CreatePasskey from './components/pages/CreatePasskey';
import EmailValidated from './components/pages/EmailValidated';
import { AuthProvider, useAuth } from './context/AuthContext';
import Landing from './components/pages/Landing';
import './App.scss';
import { useMemo } from 'react';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import BatchProcess from './components/batches/BatchProcess';
import BatchEdit from './components/batches/BatchEdit';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import ProductCosting from './components/products/ProductCosting';
import OverheadCostShow from './components/overheadCosts/OverheadCostShow';
import OverheadCostEdit from './components/overheadCosts/OverheadCostEdit';
import ConsumableShow from './components/consumables/ConsumableShow';
import ConsumableEdit from './components/consumables/ConsumableEdit';
import CustomerBulkCosting from './components/customers/CustomerBulkCosting';
import ShoppingList from './components/batches/ShoppingList';
import ProductInventoryItemList from './components/productInventoryItems/ProductInventoryItemList';
import ProductInventoryItemCreate from './components/productInventoryItems/ProductInventoryItemCreate';
import ProductInventoryItemEdit from './components/productInventoryItems/ProductInventoryItemEdit';
import CustomerBulkCostingPriceList from './components/customers/CustomerBulkCostingPriceList';
import CustomerInventory from './components/customers/CustomerInventory';
import OrderList from './components/orders/OrderList';
import OrderShow from './components/orders/OrderShow';

const ProtectedRoute = ({ children }: any) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div>
      <LinearProgress aria-label="Loading…" />
    </div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <DashboardLayout />;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <MainLayout />,
    children: [
      { index: true, element: <Landing /> },
      {
        path: '/login',
        children: [
          { index: true, element: <Login /> }
        ]
      },
      {
        path: '/register',
        children: [
          { index: true, element: <Register /> }
        ]
      },
      {
        path: '/email-validated',
        children: [
          { index: true, element: <EmailValidated /> }
        ]
      },
      { path: '*', element: <NotFound /> }
    ],
  },
  {
    path: '/dashboard',
    element: <ProtectedRoute />,
    children: [
      {
        path: '',
        children: [
          { index: true, path: '', element: <Home /> }
        ]
      },
      {
        path: 'create-passkey',
        children: [
          { index: true, element: <CreatePasskey /> }
        ]
      },
      {
        path: 'items',
        children: [
          { index: true, element: <IngredientList /> },
          { path: ':itemId', element: <IngredientShow /> },
          { path: 'new', element: <IngredientCreate /> },
          { path: ':itemId/edit', element: <IngredientEdit /> },
          { path: '*', element: <IngredientList /> }
        ]
      },
      {
        path: 'inventory-items',
        children: [
          { index: true, element: <InventoryItemList /> },
          { path: ':inventoryItemId', element: <InventoryItemShow /> },
          { path: ':inventoryItemId/edit', element: <InventoryItemEdit /> },
          { path: '*', element: <InventoryItemList /> }
        ]
      },
      {
        path: 'purchase-orders',
        children: [
          { index: true, element: <PurchaseOrderList /> },
          { path: ':purchaseOrderId', element: <PurchaseOrderShow /> },
          { path: 'new', element: <PurchaseOrderCreate /> },
          { path: '*', element: <PurchaseOrderList /> }
        ]
      },
      {
        path: 'contacts',
        children: [
          { index: true, element: <ContactList /> },
          { path: ':contactId', element: <ContactShow /> },
          { path: 'new', element: <ContactCreate /> },
          { path: ':contactId/edit', element: <ContactEdit /> },
          { path: '*', element: <ContactList /> }
        ]
      },
      {
        path: 'vendors',
        children: [
          { index: true, element: <VendorList /> },
          { path: ':vendorId', element: <VendorShow /> },
          { path: 'new', element: <VendorCreate /> },
          { path: ':vendorId/edit', element: <VendorEdit /> },
          { path: '*', element: <VendorList /> }
        ]
      },
      {
        path: 'customers',
        children: [
          { index: true, element: <CustomerList /> },
          { path: ':customerId', element: <CustomerShow /> },
          { path: 'new', element: <CustomerCreate /> },
          { path: ':customerId/edit', element: <CustomerEdit /> },
          { path: ':customerId/bulk-costing', element: <CustomerBulkCosting /> },
          { path: ':customerId/bulk-costing-price-list', element: <CustomerBulkCostingPriceList /> },
          { path: ':customerId/inventory', element: <CustomerInventory /> },
          { path: '*', element: <CustomerList /> }
        ]
      },
      {
        path: 'products',
        children: [
          { index: true, element: <ProductList /> },
          { path: ':productId', element: <ProductShow /> },
          { path: 'new', element: <ProductCreate /> },
          { path: ':productId/edit', element: <ProductEdit /> },
          { path: ':productId/costing', element: <ProductCosting /> },
          { path: '*', element: <ProductList /> },
          {
            path: ':productId/product-inventory-items',
            children: [
              { index: true, element: <ProductInventoryItemList /> },
              { path: 'new', element: <ProductInventoryItemCreate /> },
              { path: ':productInventoryItemId/edit', element: <ProductInventoryItemEdit /> },
              { path: '*', element: <ProductInventoryItemList /> }
            ]
          }
        ]
      },
      {
        path: 'batches',
        children: [
          { index: true, element: <BatchList /> },
          { path: ':batchId', element: <BatchShow /> },
          { path: ':productId/create', element: <BatchCreate /> },
          { path: ':batchId/edit', element: <BatchEdit /> },
          { path: 'process', element: <BatchProcess /> },
          { path: 'shopping-list', element: <ShoppingList /> },
          { path: '*', element: <BatchList /> }
        ]
      },
      {
        path: 'orders',
        children: [
          { index: true, element: <OrderList /> },
          { path: ':orderId', element: <OrderShow /> },
          { path: '*', element: <OrderList /> }
        ]
      },
      {
        path: 'overhead-costs',
        children: [
          { index: true, element: <OverheadCostShow /> },
          { path: 'edit', element: <OverheadCostEdit /> },
          { path: '*', element: <OverheadCostShow /> }
        ]
      },
      {
        path: 'consumables',
        children: [
          { index: true, element: <ConsumableShow /> },
          { path: 'edit', element: <ConsumableEdit /> },
          { path: '*', element: <ConsumableShow /> }
        ]
      }
    ]
  },
  { path: '*', element: <NotFound /> }
]);

function App() {
  const client = useMemo(
    () => new ApolloClient({
      link: new UploadHttpLink(
        {
          uri: `${process.env.REACT_APP_API_URL}graphql`,
          credentials: 'include',
          headers: {
            'GraphQL-preflight': '1'
          }
        }),
      cache: new InMemoryCache(),
    }), []);

  const themeComponents = {
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          '--DataGrid-overlayHeight': '300px',
          overflow: 'clip',
          borderColor: (theme.vars || theme).palette.divider,
          backgroundColor: (theme.vars || theme).palette.background.default,
          [`& .${gridClasses.columnHeader}`]: {
            backgroundColor: (theme.vars || theme).palette.background.paper,
          },
          [`& .${gridClasses.footerContainer}`]: {
            backgroundColor: (theme.vars || theme).palette.background.paper,
          },
          [`& .${checkboxClasses.root}`]: {
            padding: theme.spacing(0.5),
            '& > svg': {
              fontSize: '1rem',
            },
          },
          [`& .${tablePaginationClasses.root}`]: {
            marginRight: theme.spacing(1),
            '& .MuiIconButton-root': {
              maxHeight: 32,
              maxWidth: 32,
              '& > svg': {
                fontSize: '1rem',
              },
            },
          },
        }),
        cell: (props: any) => ({ borderTopColor: (props.theme.vars || props.theme).palette.divider }),
        menu: (props: any) => ({
          borderRadius: props.theme.shape.borderRadius,
          backgroundImage: 'none',
          [`& .${paperClasses.root}`]: {
            border: `1px solid ${(props.theme.vars || props.theme).palette.divider}`,
          },

          [`& .${menuItemClasses.root}`]: {
            margin: '0 4px',
          },
          [`& .${listItemIconClasses.root}`]: {
            marginRight: 0,
          },
          [`& .${listClasses.root}`]: {
            paddingLeft: 0,
            paddingRight: 0,
          },
        }),
        row: (props: any) => ({
          '&:last-of-type': { borderBottom: `1px solid ${(props.theme.vars || props.theme).palette.divider}` },
          '&:hover': {
            backgroundColor: (props.theme.vars || props.theme).palette.action.hover,
          },
          '&.Mui-selected': {
            background: (props.theme.vars || props.theme).palette.action.selected,
            '&:hover': {
              backgroundColor: (props.theme.vars || props.theme).palette.action.hover,
            },
          },
        }),
        iconButtonContainer: (props: any) => ({
          [`& .${iconButtonClasses.root}`]: {
            border: 'none',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: alpha(props.theme.palette.action.selected, 0.3),
            },
            '&:active': {
              backgroundColor: gray[200],
            },
            ...props.theme.applyStyles('dark', {
              color: gray[50],
              '&:hover': {
                backgroundColor: gray[800],
              },
              '&:active': {
                backgroundColor: gray[900],
              },
            }),
          },
        }),
        menuIconButton: (props: any) => ({
          border: 'none',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: gray[100],
          },
          '&:active': {
            backgroundColor: gray[200],
          },
          ...props.theme.applyStyles('dark', {
            color: gray[50],
            '&:hover': {
              backgroundColor: gray[800],
            },
            '&:active': {
              backgroundColor: gray[900],
            },
          }),
        }),
        filterForm: (props: any) => ({
          gap: props.theme.spacing(1),
          alignItems: 'flex-end',
        }),
        columnsManagementHeader: (props: any) => ({
          paddingRight: props.theme.spacing(3),
          paddingLeft: props.theme.spacing(3),
        }),
        columnHeaderTitleContainer: {
          flexGrow: 1,
          justifyContent: 'space-between',
        },
        columnHeaderDraggableContainer: { paddingRight: 2 },
        toolbar: (props: any) => ({
          backgroundColor: (props.theme.vars || props.theme).palette.background.paper,
        }),
        toolbarQuickFilter: {
          [`& .${inputBaseClasses.root}`]: {
            marginLeft: 6,
            marginRight: 6,
          },
          [`& .${iconButtonClasses.root}`]: {
            height: '36px',
            width: '36px',
          },
          [`& .${iconButtonClasses.edgeEnd}`]: {
            border: 'none',
            height: '28px',
            width: '28px',
          },
        },
      },
    },
    MuiPickerPopper: {
      styleOverrides: {
        paper: (props: any) => ({
          marginTop: 4,
          borderRadius: props.theme.shape.borderRadius,
          border: `1px solid ${(props.theme.vars || props.theme).palette.divider}`,
          backgroundImage: 'none',
          background: 'hsl(0, 0%, 100%)',
          boxShadow:
            'hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px',
          [`& .${menuItemClasses.root}`]: {
            borderRadius: 6,
            margin: '0 6px',
          },
          ...props.theme.applyStyles('dark', {
            background: gray[900],
            boxShadow:
              'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px',
          }),
        }),
      },
    },
    MuiPickersArrowSwitcher: {
      styleOverrides: {
        button: (props: any) => ({
          backgroundColor: 'transparent',
          color: (props.theme.vars || props.theme).palette.grey[500],
          ...props.theme.applyStyles('dark', {
            color: (props.theme.vars || props.theme).palette.grey[400],
          }),
        }),
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        switchViewButton: {
          padding: 0,
          border: 'none',
        },
      },
    },
    MuiMonthCalendar: {
      styleOverrides: {
        button: (props: any) => ({
          fontSize: props.theme.typography.body1.fontSize,
          color: (props.theme.vars || props.theme).palette.grey[600],
          padding: props.theme.spacing(0.5),
          borderRadius: props.theme.shape.borderRadius,
          '&:hover': {
            backgroundColor: (props.theme.vars || props.theme).palette.action.hover,
          },
          [`&.${yearCalendarClasses.selected}`]: {
            backgroundColor: gray[700],
            fontWeight: props.theme.typography.fontWeightMedium,
          },
          '&:focus': {
            outline: `3px solid ${alpha(brand[500], 0.5)}`,
            outlineOffset: '2px',
            backgroundColor: 'transparent',
            [`&.${yearCalendarClasses.selected}`]: { backgroundColor: gray[700] },
          },
          ...props.theme.applyStyles('dark', {
            color: (props.theme.vars || props.theme).palette.grey[300],
            '&:hover': {
              backgroundColor: (props.theme.vars || props.theme).palette.action.hover,
            },
            [`&.${yearCalendarClasses.selected}`]: {
              color: (props.theme.vars || props.theme).palette.common.black,
              fontWeight: props.theme.typography.fontWeightMedium,
              backgroundColor: gray[300],
            },
            '&:focus': {
              outline: `3px solid ${alpha(brand[500], 0.5)}`,
              outlineOffset: '2px',
              backgroundColor: 'transparent',
              [`&.${yearCalendarClasses.selected}`]: { backgroundColor: gray[300] },
            },
          }),
        }),
      },
    },
    MuiYearCalendar: {
      styleOverrides: {
        button: (props: any) => ({
          fontSize: props.theme.typography.body1.fontSize,
          color: (props.theme.vars || props.theme).palette.grey[600],
          padding: props.theme.spacing(0.5),
          borderRadius: props.theme.shape.borderRadius,
          height: 'fit-content',
          '&:hover': {
            backgroundColor: (props.theme.vars || props.theme).palette.action.hover,
          },
          [`&.${yearCalendarClasses.selected}`]: {
            backgroundColor: gray[700],
            fontWeight: props.theme.typography.fontWeightMedium,
          },
          '&:focus': {
            outline: `3px solid ${alpha(brand[500], 0.5)}`,
            outlineOffset: '2px',
            backgroundColor: 'transparent',
            [`&.${yearCalendarClasses.selected}`]: { backgroundColor: gray[700] },
          },
          ...props.theme.applyStyles('dark', {
            color: (props.theme.vars || props.theme).palette.grey[300],
            '&:hover': {
              backgroundColor: (props.theme.vars || props.theme).palette.action.hover,
            },
            [`&.${yearCalendarClasses.selected}`]: {
              color: (props.theme.vars || props.theme).palette.common.black,
              fontWeight: props.theme.typography.fontWeightMedium,
              backgroundColor: gray[300],
            },
            '&:focus': {
              outline: `3px solid ${alpha(brand[500], 0.5)}`,
              outlineOffset: '2px',
              backgroundColor: 'transparent',
              [`&.${yearCalendarClasses.selected}`]: { backgroundColor: gray[300] },
            },
          }),
        }),
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: (props: any) => ({
          fontSize: props.theme.typography.body1.fontSize,
          color: (props.theme.vars || props.theme).palette.grey[600],
          padding: props.theme.spacing(0.5),
          marginTop: props.theme.spacing(0.5),
          borderRadius: props.theme.shape.borderRadius,
          '&:hover': {
            backgroundColor: (props.theme.vars || props.theme).palette.action.hover,
          },
          [`&.${pickersDayClasses.selected}`]: {
            backgroundColor: gray[700],
            fontWeight: props.theme.typography.fontWeightMedium,
          },
          '&:focus': {
            outline: `3px solid ${alpha(brand[500], 0.5)}`,
            outlineOffset: '2px',
            backgroundColor: 'transparent',
            [`&.${pickersDayClasses.selected}`]: { backgroundColor: gray[700] },
          },
          ...props.theme.applyStyles('dark', {
            color: (props.theme.vars || props.theme).palette.grey[300],
            '&:hover': {
              backgroundColor: (props.theme.vars || props.theme).palette.action.hover,
            },
            [`&.${pickersDayClasses.selected}`]: {
              color: (props.theme.vars || props.theme).palette.common.black,
              fontWeight: props.theme.typography.fontWeightMedium,
              backgroundColor: gray[300],
            },
            '&:focus': {
              outline: `3px solid ${alpha(brand[500], 0.5)}`,
              outlineOffset: '2px',
              backgroundColor: 'transparent',
              [`&.${pickersDayClasses.selected}`]: { backgroundColor: gray[300] },
            },
          }),
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: (props: any) => ({
          [`& .${listSubheaderClasses.root}`]: {
            lineHeight: 3,
          },
          [`& .${listItemButtonClasses.root}`]: {
            '&.Mui-selected': {
              [`& .${typographyClasses.root}`]: {
                color: (props.theme.vars ?? props.theme).palette.text.primary,
              },
            },
          },
        }),
      },
    },
  };

  return (
    <AppTheme themeComponents={themeComponents}>
      <CssBaseline enableColorScheme />
      <NotificationsProvider>
        <DialogsProvider>
          <AuthProvider>
            <ApolloProvider client={client}>
              <RouterProvider router={router} />
            </ApolloProvider>
          </AuthProvider>
        </DialogsProvider>
      </NotificationsProvider>
    </AppTheme>
  );
}

export default App;
